import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Bus, 
  MapPin, 
  Users, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Loader2,
  Clock,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Navigation2
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { isCoachingAdmin as checkIsCoachingAdmin } from '../../utils/rbac';
import { getBatches, getBatchEnrollments, COLLECTIONS } from '../../firebase/collections';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  getDocs, 
  addDoc, 
  serverTimestamp, 
  doc, 
  updateDoc, 
  deleteDoc,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';

const TransportManagement = () => {
  const { currentUser, userProfile } = useRealtime();
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('routes'); // routes, vehicles, assignments, payments
  const [searchTerm, setSearchTerm] = useState('');
  
  // Routes Management
  const [routes, setRoutes] = useState([]);
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [routeForm, setRouteForm] = useState({
    routeName: '',
    startLocation: '',
    endLocation: '',
    stops: '',
    distance: '',
    estimatedTime: '',
    fare: ''
  });

  // Vehicles Management
  const [vehicles, setVehicles] = useState([]);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({
    vehicleNumber: '',
    vehicleType: 'bus', // bus, van, car
    capacity: '',
    driverName: '',
    driverPhone: '',
    driverLicense: '',
    isActive: true
  });

  // Student Assignments
  const [assignments, setAssignments] = useState([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    studentId: '',
    routeId: '',
    vehicleId: '',
    pickupPoint: '',
    dropPoint: '',
    startDate: new Date().toISOString().split('T')[0]
  });

  // Payments
  const [payments, setPayments] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    studentId: '',
    amount: '',
    month: new Date().toISOString().slice(0, 7),
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash',
    notes: ''
  });

  const isSuperAdmin = userProfile?.role === 'admin' || 
                       userProfile?.role === 'superadmin' || 
                       userProfile?.role === 'founder';
  const isCoachingAdmin = checkIsCoachingAdmin(userProfile, currentUser);
  
  // Only use coachingId if user is a coaching admin or staff, not a super admin
  const coachingId = isSuperAdmin ? null : (userProfile?.coachingId || null);

  useEffect(() => {
    loadBatches();
  }, [coachingId]);

  useEffect(() => {
    if (selectedBatch) {
      loadEnrollments();
    }
  }, [selectedBatch]);

  const loadBatches = async () => {
    try {
      setLoading(true);
      const result = await getBatches(coachingId || null);
      if (result.success) {
        setBatches(result.data || []);
      }
    } catch (error) {
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const loadEnrollments = async () => {
    if (!selectedBatch) return;
    try {
      const result = await getBatchEnrollments(selectedBatch.id);
      if (result.success && result.data) {
        const batchStudents = [];
        
        // Extract emails from enrollments and query users
        for (const enrollment of result.data) {
          const email = enrollment.email || enrollment.studentEmail || enrollment.userData?.email;
          if (email) {
            try {
              const userQuery = query(
                collection(db, COLLECTIONS.USERS),
                where('email', '==', email)
              );
              const userSnapshot = await getDocs(userQuery);
              
              if (!userSnapshot.empty) {
                const userDoc = userSnapshot.docs[0];
                const userData = userDoc.data();
                batchStudents.push({
                  studentId: userDoc.id,
                  studentName: userData.name || userData.displayName || userData.fullName || email,
                  studentEmail: email,
                  ...userData
                });
              }
            } catch (err) {
              console.error('Error querying user by email:', err);
            }
          }
        }
        
        setEnrollments(batchStudents);
        if (batchStudents.length === 0) {
          toast.error('No students found in this batch');
        }
      }
    } catch (error) {
      toast.error('Failed to load enrollments');
    }
  };

  const loadRoutes = useCallback(() => {
    if (!selectedBatch) return;

    let unsubscribe = null;

    // Try composite query first (requires index)
    const tryCompositeQuery = () => {
      const q = query(
        collection(db, 'transportRoutes'),
        where('batchId', '==', selectedBatch.id),
        orderBy('createdAt', 'desc')
      );

      return onSnapshot(q, (snapshot) => {
        const routesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRoutes(routesData);
      }, (error) => {
        console.error('Error loading routes (composite query):', error);
        // If index error, try fallback query
        if (error?.code === 'failed-precondition' || 
            error?.message?.includes('index') || 
            error?.message?.includes('requires an index')) {
          console.log('Composite index not available, trying fallback query...');
          if (unsubscribe) unsubscribe();
          unsubscribe = tryFallbackQuery();
        } else {
          toast.error('Failed to load routes. Please try refreshing the page.');
        }
      });
    };

    // Fallback query: query by batchId only, then sort client-side
    const tryFallbackQuery = () => {
      const fallbackQ = query(
        collection(db, 'transportRoutes'),
        where('batchId', '==', selectedBatch.id)
      );

      return onSnapshot(fallbackQ, (snapshot) => {
        const routesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort client-side by createdAt descending
        routesData.sort((a, b) => {
          const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt || 0);
          const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt || 0);
          return bTime - aTime;
        });
        setRoutes(routesData);
      }, (error) => {
        console.error('Error loading routes (fallback query):', error);
        toast.error('Failed to load routes. Please try refreshing the page.');
      });
    };

    // Start with composite query
    unsubscribe = tryCompositeQuery();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedBatch]);

  const loadVehicles = useCallback(() => {
    if (!selectedBatch) return;

    let unsubscribe = null;

    // Try composite query first (requires index)
    const tryCompositeQuery = () => {
      const q = query(
        collection(db, 'transportVehicles'),
        where('batchId', '==', selectedBatch.id),
        orderBy('createdAt', 'desc')
      );

      return onSnapshot(q, (snapshot) => {
        const vehiclesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setVehicles(vehiclesData);
      }, (error) => {
        console.error('Error loading vehicles (composite query):', error);
        // If index error, try fallback query
        if (error?.code === 'failed-precondition' || 
            error?.message?.includes('index') || 
            error?.message?.includes('requires an index')) {
          console.log('Composite index not available, trying fallback query...');
          if (unsubscribe) unsubscribe();
          unsubscribe = tryFallbackQuery();
        } else {
          toast.error('Failed to load vehicles. Please try refreshing the page.');
        }
      });
    };

    // Fallback query: query by batchId only, then sort client-side
    const tryFallbackQuery = () => {
      const fallbackQ = query(
        collection(db, 'transportVehicles'),
        where('batchId', '==', selectedBatch.id)
      );

      return onSnapshot(fallbackQ, (snapshot) => {
        const vehiclesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort client-side by createdAt descending
        vehiclesData.sort((a, b) => {
          const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt || 0);
          const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt || 0);
          return bTime - aTime;
        });
        setVehicles(vehiclesData);
      }, (error) => {
        console.error('Error loading vehicles (fallback query):', error);
        toast.error('Failed to load vehicles. Please try refreshing the page.');
      });
    };

    // Start with composite query
    unsubscribe = tryCompositeQuery();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedBatch]);

  const loadAssignments = useCallback(() => {
    if (!selectedBatch) return;

    let unsubscribe = null;

    // Try composite query first (requires index)
    const tryCompositeQuery = () => {
      const q = query(
        collection(db, 'transportAssignments'),
        where('batchId', '==', selectedBatch.id),
        orderBy('createdAt', 'desc')
      );

      return onSnapshot(q, (snapshot) => {
        const assignmentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAssignments(assignmentsData);
      }, (error) => {
        console.error('Error loading assignments (composite query):', error);
        // If index error, try fallback query
        if (error?.code === 'failed-precondition' || 
            error?.message?.includes('index') || 
            error?.message?.includes('requires an index')) {
          console.log('Composite index not available, trying fallback query...');
          if (unsubscribe) unsubscribe();
          unsubscribe = tryFallbackQuery();
        } else {
          toast.error('Failed to load assignments. Please try refreshing the page.');
        }
      });
    };

    // Fallback query: query by batchId only, then sort client-side
    const tryFallbackQuery = () => {
      const fallbackQ = query(
        collection(db, 'transportAssignments'),
        where('batchId', '==', selectedBatch.id)
      );

      return onSnapshot(fallbackQ, (snapshot) => {
        const assignmentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort client-side by createdAt descending
        assignmentsData.sort((a, b) => {
          const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt || 0);
          const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt || 0);
          return bTime - aTime;
        });
        setAssignments(assignmentsData);
      }, (error) => {
        console.error('Error loading assignments (fallback query):', error);
        toast.error('Failed to load assignments. Please try refreshing the page.');
      });
    };

    // Start with composite query
    unsubscribe = tryCompositeQuery();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedBatch]);

  const loadPayments = useCallback(() => {
    if (!selectedBatch) return;

    let unsubscribe = null;

    // Try composite query first (requires index)
    const tryCompositeQuery = () => {
      const q = query(
        collection(db, 'transportPayments'),
        where('batchId', '==', selectedBatch.id),
        orderBy('paymentDate', 'desc')
      );

      return onSnapshot(q, (snapshot) => {
        const paymentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setPayments(paymentsData);
      }, (error) => {
        console.error('Error loading payments (composite query):', error);
        // If index error, try fallback query
        if (error?.code === 'failed-precondition' || 
            error?.message?.includes('index') || 
            error?.message?.includes('requires an index')) {
          console.log('Composite index not available, trying fallback query...');
          if (unsubscribe) unsubscribe();
          unsubscribe = tryFallbackQuery();
        } else {
          toast.error('Failed to load payments. Please try refreshing the page.');
        }
      });
    };

    // Fallback query: query by batchId only, then sort client-side
    const tryFallbackQuery = () => {
      const fallbackQ = query(
        collection(db, 'transportPayments'),
        where('batchId', '==', selectedBatch.id)
      );

      return onSnapshot(fallbackQ, (snapshot) => {
        const paymentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort client-side by paymentDate descending
        paymentsData.sort((a, b) => {
          const aTime = a.paymentDate?.toDate ? a.paymentDate.toDate().getTime() : (a.paymentDate || 0);
          const bTime = b.paymentDate?.toDate ? b.paymentDate.toDate().getTime() : (b.paymentDate || 0);
          return bTime - aTime;
        });
        setPayments(paymentsData);
      }, (error) => {
        console.error('Error loading payments (fallback query):', error);
        toast.error('Failed to load payments. Please try refreshing the page.');
      });
    };

    // Start with composite query
    unsubscribe = tryCompositeQuery();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedBatch]);

  // Set up real-time listeners after functions are defined
  useEffect(() => {
    if (selectedBatch) {
      const unsubscribeRoutes = loadRoutes();
      const unsubscribeVehicles = loadVehicles();
      const unsubscribeAssignments = loadAssignments();
      const unsubscribePayments = loadPayments();
      
      return () => {
        if (unsubscribeRoutes) unsubscribeRoutes();
        if (unsubscribeVehicles) unsubscribeVehicles();
        if (unsubscribeAssignments) unsubscribeAssignments();
        if (unsubscribePayments) unsubscribePayments();
      };
    }
  }, [selectedBatch, loadRoutes, loadVehicles, loadAssignments, loadPayments]);

  const handleSaveRoute = async () => {
    if (!selectedBatch || !routeForm.routeName || !routeForm.startLocation || !routeForm.endLocation) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const routeData = {
        batchId: selectedBatch.id,
        routeName: routeForm.routeName,
        startLocation: routeForm.startLocation,
        endLocation: routeForm.endLocation,
        stops: routeForm.stops.split(',').map(stop => stop.trim()).filter(Boolean),
        distance: routeForm.distance ? parseFloat(routeForm.distance) : null,
        estimatedTime: routeForm.estimatedTime || null,
        fare: routeForm.fare ? parseFloat(routeForm.fare) : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (editingRoute) {
        await updateDoc(doc(db, 'transportRoutes', editingRoute.id), {
          ...routeData,
          updatedAt: serverTimestamp()
        });
        toast.success('Route updated successfully');
      } else {
        await addDoc(collection(db, 'transportRoutes'), routeData);
        toast.success('Route added successfully');
      }

      setShowRouteModal(false);
      setEditingRoute(null);
      setRouteForm({
        routeName: '',
        startLocation: '',
        endLocation: '',
        stops: '',
        distance: '',
        estimatedTime: '',
        fare: ''
      });
    } catch (error) {
      toast.error('Failed to save route');
    }
  };

  const handleSaveVehicle = async () => {
    if (!selectedBatch || !vehicleForm.vehicleNumber || !vehicleForm.driverName) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const vehicleData = {
        batchId: selectedBatch.id,
        vehicleNumber: vehicleForm.vehicleNumber,
        vehicleType: vehicleForm.vehicleType,
        capacity: vehicleForm.capacity ? parseInt(vehicleForm.capacity) : null,
        driverName: vehicleForm.driverName,
        driverPhone: vehicleForm.driverPhone || null,
        driverLicense: vehicleForm.driverLicense || null,
        isActive: vehicleForm.isActive,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (editingVehicle) {
        await updateDoc(doc(db, 'transportVehicles', editingVehicle.id), {
          ...vehicleData,
          updatedAt: serverTimestamp()
        });
        toast.success('Vehicle updated successfully');
      } else {
        await addDoc(collection(db, 'transportVehicles'), vehicleData);
        toast.success('Vehicle added successfully');
      }

      setShowVehicleModal(false);
      setEditingVehicle(null);
      setVehicleForm({
        vehicleNumber: '',
        vehicleType: 'bus',
        capacity: '',
        driverName: '',
        driverPhone: '',
        driverLicense: '',
        isActive: true
      });
    } catch (error) {
      toast.error('Failed to save vehicle');
    }
  };

  const handleSaveAssignment = async () => {
    if (!selectedBatch || !assignmentForm.studentId || !assignmentForm.routeId) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const student = enrollments.find(e => (e.studentId || e.userId) === assignmentForm.studentId);
      if (!student) {
        toast.error('Student not found');
        return;
      }

      // Get route and vehicle details
      const route = routes.find(r => r.id === assignmentForm.routeId);
      const vehicle = assignmentForm.vehicleId ? vehicles.find(v => v.id === assignmentForm.vehicleId) : null;

      const assignmentData = {
        batchId: selectedBatch.id,
        studentId: assignmentForm.studentId,
        studentName: student.studentName,
        studentEmail: student.studentEmail,
        routeId: assignmentForm.routeId,
        vehicleId: assignmentForm.vehicleId || null,
        pickupPoint: assignmentForm.pickupPoint || null,
        dropPoint: assignmentForm.dropPoint || null,
        startDate: assignmentForm.startDate,
        isActive: true,
        createdAt: serverTimestamp()
      };

      // Save to transportAssignments collection
      await addDoc(collection(db, 'transportAssignments'), assignmentData);

      // Also save/update to studentTransport collection for student view
      // Check if student already has a transport assignment
      const studentTransportQuery = query(
        collection(db, 'studentTransport'),
        where('batchId', '==', selectedBatch.id),
        where('studentId', '==', assignmentForm.studentId)
      );
      const existingTransportSnapshot = await getDocs(studentTransportQuery);
      
      const studentTransportData = {
        batchId: selectedBatch.id,
        studentId: assignmentForm.studentId,
        studentName: student.studentName,
        studentEmail: student.studentEmail,
        routeId: assignmentForm.routeId,
        routeName: route?.routeName || '',
        startLocation: route?.startLocation || '',
        endLocation: route?.endLocation || '',
        stops: route?.stops || [],
        vehicleId: assignmentForm.vehicleId || null,
        vehicleNumber: vehicle?.vehicleNumber || null,
        vehicleType: vehicle?.vehicleType || null,
        driverName: vehicle?.driverName || null,
        driverPhone: vehicle?.driverPhone || null,
        pickupPoint: assignmentForm.pickupPoint || null,
        dropPoint: assignmentForm.dropPoint || null,
        startDate: assignmentForm.startDate,
        isActive: true,
        updatedAt: serverTimestamp()
      };

      if (!existingTransportSnapshot.empty) {
        // Update existing record
        const existingDoc = existingTransportSnapshot.docs[0];
        await updateDoc(doc(db, 'studentTransport', existingDoc.id), studentTransportData);
      } else {
        // Create new record
        await addDoc(collection(db, 'studentTransport'), {
          ...studentTransportData,
          createdAt: serverTimestamp()
        });
      }

      toast.success('Assignment created successfully');
      setShowAssignmentModal(false);
      setAssignmentForm({
        studentId: '',
        routeId: '',
        vehicleId: '',
        pickupPoint: '',
        dropPoint: '',
        startDate: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      toast.error('Failed to create assignment');
    }
  };

  const handleSavePayment = async () => {
    if (!selectedBatch || !paymentForm.studentId || !paymentForm.amount) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const student = enrollments.find(e => (e.studentId || e.userId) === paymentForm.studentId);
      if (!student) {
        toast.error('Student not found');
        return;
      }

      const paymentData = {
        batchId: selectedBatch.id,
        studentId: paymentForm.studentId,
        studentName: student.studentName,
        studentEmail: student.studentEmail,
        amount: parseFloat(paymentForm.amount),
        month: paymentForm.month,
        paymentDate: paymentForm.paymentDate,
        paymentMethod: paymentForm.paymentMethod,
        notes: paymentForm.notes,
        recordedBy: userProfile?.uid,
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'transportPayments'), paymentData);
      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      setPaymentForm({
        studentId: '',
        amount: '',
        month: new Date().toISOString().slice(0, 7),
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'cash',
        notes: ''
      });
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const filteredEnrollments = enrollments.filter(enrollment => {
    const studentName = enrollment.studentName || '';
    const studentEmail = enrollment.studentEmail || '';
    return studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           studentEmail.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
          <Bus className="w-8 h-8 mr-3 text-blue-600" /> Transport Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">Manage transport routes, vehicles, and student assignments</p>
      </div>

      {/* Batch Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Batch
        </label>
        <select
          value={selectedBatch?.id || ''}
          onChange={(e) => {
            const batch = batches.find(b => b.id === e.target.value);
            setSelectedBatch(batch);
          }}
          className="w-full md:w-1/3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
        >
          <option value="">Select a batch</option>
          {batches.map((batch) => (
            <option key={batch.id} value={batch.id}>
              {batch.name}
            </option>
          ))}
        </select>
      </div>

      {selectedBatch && (
        <>
          {/* Tabs */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex -mb-px">
                <button
                  onClick={() => setActiveTab('routes')}
                  className={`py-4 px-6 border-b-2 font-medium text-sm ${
                    activeTab === 'routes'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Routes
                </button>
                <button
                  onClick={() => setActiveTab('vehicles')}
                  className={`py-4 px-6 border-b-2 font-medium text-sm ${
                    activeTab === 'vehicles'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Vehicles
                </button>
                <button
                  onClick={() => setActiveTab('assignments')}
                  className={`py-4 px-6 border-b-2 font-medium text-sm ${
                    activeTab === 'assignments'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Assignments
                </button>
                <button
                  onClick={() => setActiveTab('payments')}
                  className={`py-4 px-6 border-b-2 font-medium text-sm ${
                    activeTab === 'payments'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Payments
                </button>
              </nav>
            </div>

            <div className="p-6">
              {/* Routes Tab */}
              {activeTab === 'routes' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Transport Routes</h2>
                    <button
                      onClick={() => {
                        setEditingRoute(null);
                        setRouteForm({
                          routeName: '',
                          startLocation: '',
                          endLocation: '',
                          stops: '',
                          distance: '',
                          estimatedTime: '',
                          fare: ''
                        });
                        setShowRouteModal(true);
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Route</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {routes.map((route) => (
                      <div key={route.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white">{route.routeName}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <MapPin className="w-4 h-4 inline mr-1" />
                              {route.startLocation} → {route.endLocation}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setEditingRoute(route);
                              setRouteForm({
                                routeName: route.routeName,
                                startLocation: route.startLocation,
                                endLocation: route.endLocation,
                                stops: route.stops?.join(', ') || '',
                                distance: route.distance?.toString() || '',
                                estimatedTime: route.estimatedTime || '',
                                fare: route.fare?.toString() || ''
                              });
                              setShowRouteModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        </div>
                        {route.stops && route.stops.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Stops:</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                              {route.stops.join(', ')}
                            </p>
                          </div>
                        )}
                        <div className="mt-2 flex justify-between text-sm">
                          {route.distance && (
                            <span className="text-gray-600 dark:text-gray-400">
                              Distance: {route.distance} km
                            </span>
                          )}
                          {route.fare && (
                            <span className="font-semibold text-gray-900 dark:text-white">
                              Fare: ₹{route.fare}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {routes.length === 0 && (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <Navigation2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No routes found. Add a route to get started.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Vehicles Tab */}
              {activeTab === 'vehicles' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Vehicles</h2>
                    <button
                      onClick={() => {
                        setEditingVehicle(null);
                        setVehicleForm({
                          vehicleNumber: '',
                          vehicleType: 'bus',
                          capacity: '',
                          driverName: '',
                          driverPhone: '',
                          driverLicense: '',
                          isActive: true
                        });
                        setShowVehicleModal(true);
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Vehicle</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {vehicles.map((vehicle) => (
                      <div key={vehicle.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white">
                              {vehicle.vehicleNumber}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                              {vehicle.vehicleType}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              vehicle.isActive 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300'
                            }`}>
                              {vehicle.isActive ? 'Active' : 'Inactive'}
                            </span>
                            <button
                              onClick={() => {
                                setEditingVehicle(vehicle);
                                setVehicleForm({
                                  vehicleNumber: vehicle.vehicleNumber,
                                  vehicleType: vehicle.vehicleType,
                                  capacity: vehicle.capacity?.toString() || '',
                                  driverName: vehicle.driverName,
                                  driverPhone: vehicle.driverPhone || '',
                                  driverLicense: vehicle.driverLicense || '',
                                  isActive: vehicle.isActive
                                });
                                setShowVehicleModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 space-y-1 text-sm">
                          <p className="text-gray-700 dark:text-gray-300">
                            <strong>Driver:</strong> {vehicle.driverName}
                          </p>
                          {vehicle.driverPhone && (
                            <p className="text-gray-600 dark:text-gray-400">
                              Phone: {vehicle.driverPhone}
                            </p>
                          )}
                          {vehicle.capacity && (
                            <p className="text-gray-600 dark:text-gray-400">
                              Capacity: {vehicle.capacity} seats
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {vehicles.length === 0 && (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <Bus className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No vehicles found. Add a vehicle to get started.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Assignments Tab */}
              {activeTab === 'assignments' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Student Assignments</h2>
                    <button
                      onClick={() => {
                        setAssignmentForm({
                          studentId: '',
                          routeId: '',
                          vehicleId: '',
                          pickupPoint: '',
                          dropPoint: '',
                          startDate: new Date().toISOString().split('T')[0]
                        });
                        setShowAssignmentModal(true);
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Assign Student</span>
                    </button>
                  </div>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      placeholder="Search students..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            Student
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            Route
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            Vehicle
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            Pickup Point
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {assignments.map((assignment) => {
                          const route = routes.find(r => r.id === assignment.routeId);
                          const vehicle = vehicles.find(v => v.id === assignment.vehicleId);
                          return (
                            <tr key={assignment.id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {assignment.studentName}
                                  </p>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {assignment.studentEmail}
                                  </p>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <p className="text-sm text-gray-900 dark:text-white">
                                  {route?.routeName || 'N/A'}
                                </p>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <p className="text-sm text-gray-900 dark:text-white">
                                  {vehicle?.vehicleNumber || 'N/A'}
                                </p>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {assignment.pickupPoint || '-'}
                                </p>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  assignment.isActive 
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300'
                                }`}>
                                  {assignment.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {assignments.length === 0 && (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No assignments found. Assign a student to get started.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Payments Tab */}
              {activeTab === 'payments' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Transport Payments</h2>
                    <button
                      onClick={() => {
                        setPaymentForm({
                          studentId: '',
                          amount: '',
                          month: new Date().toISOString().slice(0, 7),
                          paymentDate: new Date().toISOString().split('T')[0],
                          paymentMethod: 'cash',
                          notes: ''
                        });
                        setShowPaymentModal(true);
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Record Payment</span>
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            Student
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            Month
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            Payment Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                            Method
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {payments.map((payment) => (
                          <tr key={payment.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {payment.studentName}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {payment.studentEmail}
                                </p>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                ₹{payment.amount}
                              </p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {new Date(payment.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                              </p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : '-'}
                              </p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 capitalize">
                                {payment.paymentMethod}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {payments.length === 0 && (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No payments recorded yet.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Route Modal */}
      {showRouteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingRoute ? 'Edit Route' : 'Add Route'}
              </h3>
              <button onClick={() => setShowRouteModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Route Name *
                </label>
                <input
                  type="text"
                  value={routeForm.routeName}
                  onChange={(e) => setRouteForm({ ...routeForm, routeName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Location *
                  </label>
                  <input
                    type="text"
                    value={routeForm.startLocation}
                    onChange={(e) => setRouteForm({ ...routeForm, startLocation: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Location *
                  </label>
                  <input
                    type="text"
                    value={routeForm.endLocation}
                    onChange={(e) => setRouteForm({ ...routeForm, endLocation: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Stops (comma separated)
                </label>
                <input
                  type="text"
                  value={routeForm.stops}
                  onChange={(e) => setRouteForm({ ...routeForm, stops: e.target.value })}
                  placeholder="Stop 1, Stop 2, Stop 3"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Distance (km)
                  </label>
                  <input
                    type="number"
                    value={routeForm.distance}
                    onChange={(e) => setRouteForm({ ...routeForm, distance: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Time
                  </label>
                  <input
                    type="text"
                    value={routeForm.estimatedTime}
                    onChange={(e) => setRouteForm({ ...routeForm, estimatedTime: e.target.value })}
                    placeholder="e.g., 30 min"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fare (₹)
                  </label>
                  <input
                    type="number"
                    value={routeForm.fare}
                    onChange={(e) => setRouteForm({ ...routeForm, fare: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowRouteModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRoute}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Modal */}
      {showVehicleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}
              </h3>
              <button onClick={() => setShowVehicleModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vehicle Number *
                </label>
                <input
                  type="text"
                  value={vehicleForm.vehicleNumber}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, vehicleNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Vehicle Type
                  </label>
                  <select
                    value={vehicleForm.vehicleType}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, vehicleType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  >
                    <option value="bus">Bus</option>
                    <option value="van">Van</option>
                    <option value="car">Car</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Capacity
                  </label>
                  <input
                    type="number"
                    value={vehicleForm.capacity}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, capacity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Driver Name *
                </label>
                <input
                  type="text"
                  value={vehicleForm.driverName}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, driverName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Driver Phone
                  </label>
                  <input
                    type="tel"
                    value={vehicleForm.driverPhone}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, driverPhone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Driver License
                  </label>
                  <input
                    type="text"
                    value={vehicleForm.driverLicense}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, driverLicense: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={vehicleForm.isActive}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active
                </label>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowVehicleModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveVehicle}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Assign Student</h3>
              <button onClick={() => setShowAssignmentModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Student *
                </label>
                <select
                  value={assignmentForm.studentId}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, studentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select a student</option>
                  {enrollments.map((enrollment) => (
                    <option key={enrollment.studentId || enrollment.userId} value={enrollment.studentId || enrollment.userId}>
                      {enrollment.studentName} ({enrollment.studentEmail})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Route *
                </label>
                <select
                  value={assignmentForm.routeId}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, routeId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select a route</option>
                  {routes.map((route) => (
                    <option key={route.id} value={route.id}>
                      {route.routeName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vehicle
                </label>
                <select
                  value={assignmentForm.vehicleId}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, vehicleId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select a vehicle</option>
                  {vehicles.filter(v => v.isActive).map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.vehicleNumber} ({vehicle.vehicleType})
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Pickup Point
                  </label>
                  <input
                    type="text"
                    value={assignmentForm.pickupPoint}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, pickupPoint: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Drop Point
                  </label>
                  <input
                    type="text"
                    value={assignmentForm.dropPoint}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, dropPoint: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={assignmentForm.startDate}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowAssignmentModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAssignment}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                >
                  Assign
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal - Same as Mess Management */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Record Payment</h3>
              <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Student
                </label>
                <select
                  value={paymentForm.studentId}
                  onChange={(e) => setPaymentForm({ ...paymentForm, studentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select a student</option>
                  {enrollments.map((enrollment) => (
                    <option key={enrollment.studentId || enrollment.userId} value={enrollment.studentId || enrollment.userId}>
                      {enrollment.studentName} ({enrollment.studentEmail})
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Month
                  </label>
                  <input
                    type="month"
                    value={paymentForm.month}
                    onChange={(e) => setPaymentForm({ ...paymentForm, month: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    value={paymentForm.paymentDate}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Payment Method
                  </label>
                  <select
                    value={paymentForm.paymentMethod}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  >
                    <option value="cash">Cash</option>
                    <option value="online">Online</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePayment}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                >
                  Record Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportManagement;

